import api from "./client";
import axios from "axios";
import type { Localizacao } from "./estoque";


// ==================== REPOSIÇÃO ====================

export type StatusReposicao =
	| "pendente"
	| "em_andamento"
	| "concluido"
	| "cancelado";


export type MotivoReposicao =
	| "reposicao pedido"
	| "reposicao sob demanda"
	| "reposicao de segurança";


export interface CreateReposicaoOrigemPayload {
	produto_estoque_id: number;
	quantidade: number;
}


export interface CreateReposicaoPayload {
	localizacao_destino_id: number;
	motivo_reposicao?: MotivoReposicao;
	status?: StatusReposicao;
	produto_id?: number;
	quantidade_reposicao?: number;
	/**
	 * Origens explícitas escolhidas pelo operador no modal de reposição
	 * interna (abas "Por produto" / "Por localização"). Quando informado,
	 * o backend pula o greedy automático. Soma das quantidades deve bater
	 * com `quantidade_reposicao`.
	 */
	origens?: CreateReposicaoOrigemPayload[];
}


export const criarReposicao = async (payload: CreateReposicaoPayload) => {
	try {
		const { data } = await api.post("/reposicao", payload);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao criar solicitação de reposição.");
	}
};


// ==================== ORIGENS DISPONÍVEIS (Modal 3 abas) ====================

export interface OrigemDisponivel {
	produto_estoque_id: number;
	localizacao_id: number;
	localizacao_nome: string;
	tipo_origem: string;
	/**
	 * TETO do que dá para retirar: `quantidade − reservado TRAVADO`.
	 * Reserva de consumidor que ainda não começou não reduz o teto — ela é
	 * realocada na criação.
	 */
	quantidade_disponivel: number;
	/** Saldo sem reserva nenhuma — referência ao lado do teto. */
	saldo_livre?: number;
	/** Reservado ativo total (as 5 fontes). */
	reservado?: number;
	/** Parte do reservado que a reposição consegue mover. */
	reservado_realocavel?: number;
	/** Parte intocável (consumidor em campo) — é o que reduz o teto. */
	reservado_travado?: number;
	/** Quem travou, para o tooltip. */
	motivo_travado?: string | null;
	/** Origem com ocorrência ativa (A1) ou em envio (A2): aparece, mas não pode ser usada. */
	bloqueado?: boolean;
	/** Motivo do bloqueio para o aviso vermelho; null/undefined se livre. */
	motivo_bloqueio?: string | null;
}


export const buscarOrigensPorProduto = async (params: {
	produto_id: number;
	armazem_id: number;
	// Opcional (Gap A "iniciar por produto"): sem destino, lista todas as
	// localizações do armazém com o produto.
	localizacao_destino_id?: number;
}): Promise<{ origens: OrigemDisponivel[] }> => {
	const { data } = await api.get("/reposicao/origens/por-produto", { params });
	return data;
};


export const buscarOrigensPorLocalizacao = async (params: {
	produto_id: number;
	armazem_id: number;
	localizacao_destino_id: number;
}): Promise<{ origens: OrigemDisponivel[] }> => {
	const { data } = await api.get("/reposicao/origens/por-localizacao", {
		params,
	});
	return data;
};


// ============ PRODUTOS DA ORIGEM (tabela "Por Origem") ============

export interface ProdutoDaLocalizacaoOrigem {
	produto_estoque_id: number;
	produto_id: number;
	sku: string;
	descricao: string;
	ean: string;
	quantidade: number;
	/** Reservado ativo somando as 5 fontes (inclui reposição/transferência). */
	reservado: number;
	/** `quantidade − reservado`, nunca negativo. */
	saldo_livre: number;
	/**
	 * Reserva que a reposição interna NÃO pode mexer: o consumidor já está em
	 * campo (tarefa em andamento / operação em andamento).
	 */
	reservado_travado: number;
	/** Reserva que SERÁ REALOCADA se esta origem for usada acima do saldo livre. */
	reservado_realocavel: number;
	/** Quem travou (num_pedido / num_envio / "Reposição #N"), para o tooltip. */
	motivo_travado: string | null;
	/**
	 * TETO do input: `quantidade − reservado_travado`.
	 *
	 * Use ESTE como `max`, não `saldo_livre`. A diferença entre os dois é o que
	 * a realocação de reserva liberou — pedir acima do saldo livre é permitido e
	 * move a reserva de quem ainda não começou.
	 */
	saldo_utilizavel: number;
}

/**
 * Produtos de uma localização de origem com o saldo livre REAL.
 *
 * Não usar `GET /localizacao/:id/produtos` aqui: aquele endpoint devolve a
 * coluna cache `estoque_reservado`, que não enxerga a reserva de reposição /
 * transferência (derivada do status do header, nunca materializada). A tela
 * mostrava como livre um estoque já comprometido.
 */
export const buscarProdutosDaOrigem = async (
	localizacao_id: number,
): Promise<ProdutoDaLocalizacaoOrigem[]> => {
	const { data } = await api.get("/reposicao/origens/produtos-por-localizacao", {
		params: { localizacao_id },
	});
	return Array.isArray(data) ? data : [];
};

// ==================== ESTOQUE POR TIPO (tabela "Por Origem") ====================

export interface EstoquePorTipoItem {
	produto_id: number;
	/** Mapa `tipo (lowercase) → soma da quantidade` no armazém. */
	por_tipo: Record<string, number>;
}

/**
 * Para os produtos existentes na origem selecionada, soma o estoque por TIPO
 * de localização no armazém (ex.: total em todas as prateleiras, todos os
 * cestos). Alimenta as colunas específicas da tabela "Por Origem" da
 * criação de transferência.
 */
export const buscarEstoquePorTipo = async (params: {
	armazem_id: number;
	produto_ids: number[];
}): Promise<EstoquePorTipoItem[]> => {
	if (!params.produto_ids || params.produto_ids.length === 0) return [];
	const { data } = await api.get("/reposicao/origens/estoque-por-tipo", {
		params: {
			armazem_id: params.armazem_id,
			produto_ids: params.produto_ids.join(","),
		},
	});
	return data;
};


// ==================== DESTINOS ELEGÍVEIS (Gap C) ====================

export interface DestinoElegivel {
	localizacao_id: number;
	localizacao_nome: string;
	/** EAN da etiqueta da localização — permite bipar o destino (seleção direta). */
	ean: string | null;
	tipo_destino: string;
	armazem_nome: string;
	/** True se a localização já tem o produto (informativo — a interna aceita SKU novo). */
	ja_tem_produto: boolean;
	/** Destino com ocorrência ativa (A1) ou em envio (A2): aparece, mas bloqueia a criação. */
	bloqueado: boolean;
	/** Motivo do bloqueio para o aviso vermelho; null se livre. */
	motivo_bloqueio: string | null;
}


/**
 * Destinos elegíveis a partir de uma origem (inverso de `buscarOrigensPorProduto`).
 * Informe `produto_estoque_origem_id` OU (`produto_id` + `armazem_id`).
 */
export const buscarDestinosPorOrigem = async (params: {
	produto_estoque_origem_id?: number;
	produto_id?: number;
	armazem_id?: number;
	busca?: string;
}): Promise<{ destinos: DestinoElegivel[] }> => {
	const { data } = await api.get("/reposicao/destinos/por-origem", { params });
	return data;
};


/**
 * Busca de localização (nome/EAN) para os seletores genéricos do modal de
 * reposição interna — origem no "Por origem" e destino nos "Por destino"/"Por
 * produto". Diferente do `buscarLocalizacoes` genérico, ESCONDE os tipos vetados
 * em "Bloqueios na reposição interna" (server-side, sem truncar no LIMIT).
 * Mesmo formato de retorno do `buscarLocalizacoes`.
 */
export const buscarLocalizacoesReposicao = async (
	limit: number,
	offset: number,
	busca?: string,
	armazemId?: number,
): Promise<{ results: Localizacao[] }> => {
	const { data } = await api.get("/reposicao/destinos/localizacoes", {
		params: { limit, offset, busca, armazem_id: armazemId },
	});
	return data;
};

export interface BloqueioLocalizacao {
	bloqueado: boolean;
	motivo_bloqueio: string | null;
}


/**
 * Bloqueio (ocorrência/envio) de uma localização de destino — para os modos
 * "Por destino"/"Por produto" avisarem antes de salvar (o destino vem de busca
 * livre, sem o flag da lista).
 */
export const buscarBloqueioLocalizacao = async (
	localizacao_id: number,
): Promise<BloqueioLocalizacao> => {
	const { data } = await api.get(
		`/reposicao/destinos/bloqueio/${localizacao_id}`,
	);
	return data;
};


// ==================== DIFERENÇA DE PRODUTOS (Gap D) ====================

export interface DiferencaProduto {
	produto_id: number;
	sku: string;
	descricao: string;
	saldo_origem: number;
	origens: Array<{
		produto_estoque_id: number;
		localizacao_id: number;
		localizacao_nome: string;
		quantidade: number;
		/** Origem com ocorrência ativa (A1) ou em envio (A2) — exibir em vermelho. */
		bloqueado?: boolean;
		motivo_bloqueio?: string | null;
	}>;
}


/**
 * Produtos que existem na origem e faltam no destino (candidatos a reposição).
 * Origem e destino podem ser uma localização específica OU um tipo. Paginado
 * (offset/limit) — `total` é a contagem sem paginação.
 */
export const buscarDiferencaProdutos = async (params: {
	armazem_id: number;
	origem_localizacao_id?: number;
	origem_tipo?: string;
	destino_localizacao_id?: number;
	destino_tipo?: string;
	offset?: number;
	limit?: number;
}): Promise<{ produtos: DiferencaProduto[]; total: number }> => {
	const { data } = await api.get("/reposicao/diferenca-produtos", { params });
	return data;
};


// ==================== LOTE / FAN-OUT MULTI-DESTINO (Gap B) ====================

export interface CreateReposicaoOperacaoPayload {
	localizacao_destino_id: number;
	produto_id: number;
	quantidade_reposicao: number;
	origens: CreateReposicaoOrigemPayload[];
}


export interface CreateReposicaoLotePayload {
	motivo_reposicao?: MotivoReposicao;
	operacoes: CreateReposicaoOperacaoPayload[];
}


/**
 * Cria N reposições de destino único numa operação (fan-out). All-or-nothing:
 * se qualquer operação falhar, o backend desfaz as criadas. Todas compartilham
 * o `lote_id` retornado.
 */
export const criarReposicaoLote = async (
	payload: CreateReposicaoLotePayload,
): Promise<{ lote_id: string; reposicoes: Array<{ reposicao_id: number }> }> => {
	try {
		const { data } = await api.post("/reposicao/lote", payload);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao criar reposições em lote.");
	}
};


export interface BuscarReposicoesResponse {
	results: Array<any>;
	total?: { limit?: number; offset?: number; total_itens?: number };
}


export const buscarReposicoes = async (dados: {
	search?: string;
	status?: string;
	motivo?: string;
	pedido_numero?: string;
	localizacao_id?: string;
	armazem_id?: string;
	offset?: number;
	limit?: number;
}): Promise<BuscarReposicoesResponse> => {
	try {
		const params: Record<string, string> = {};

		if (dados.search) params.search = dados.search;
		if (dados.status) params.status = dados.status;
		if (dados.motivo) params.motivo = dados.motivo;
		if (dados.localizacao_id) params.localizacao_id = dados.localizacao_id;
		if (dados.armazem_id) params.armazem_id = dados.armazem_id;
		if (dados.pedido_numero) params.pedido_numero = dados.pedido_numero;
		if (dados.offset !== undefined) params.offset = String(dados.offset);
		if (dados.limit !== undefined) params.limit = String(dados.limit);

		const response = await api.get("/reposicao", { params });
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar reposições:", error?.message || error);
		throw new Error("Falha ao buscar reposições.");
	}
};


export interface CreateAbastecimentoArmazemPayload {
	armazem_id: number;
	produto_id: number;
	quantidade_total_desejada: number;
}


export const criarAbastecimentoArmazem = async (
	payload: CreateAbastecimentoArmazemPayload,
) => {
	try {
		const { data } = await api.post(
			"/reposicao/abastecimento-armazem",
			payload,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao criar abastecimento de armazém.");
	}
};


export interface EstoqueDisponivelPorArmazem {
	produto_id: number;
	estoque_total: number;
	estoque_disponivel_total: number;
	armazens: Array<{
		armazem_id: number;
		nome: string;
		estoque_total: number;
		estoque_disponivel: number;
		localizacoes: Array<{
			localizacao_id: number;
			nome: string;
			tipo: string;
			quantidade: number;
			quantidade_disponivel: number;
		}>;
	}>;
}


export const buscarEstoqueDisponivelPorArmazem = async (
	produto_id: number,
): Promise<EstoqueDisponivelPorArmazem> => {
	try {
		const { data } = await api.get(
			`/reposicao/estoque-disponivel/${produto_id}`,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao buscar estoque disponível por armazém.");
	}
};


// ---------- AMOSTRAGEM DE REPOSIÇÃO ----------

export interface AmostragemItem {
	amostragem_id: number;
	item_seq: number;
	localizacao_id: number;
	localizacao_nome: string | null;
	produto_id: number;
	produto_sku: string | null;
	quantidade_informada: number;
	quantidade_bipada: number | null;
	aprovado: boolean | null;
	observacao: string | null;
}


export interface Amostragem {
	amostragem_id: number;
	status: "pendente" | "em_andamento" | "aprovada" | "reprovada";
	total_caixas: number;
	total_amostras: number;
	amostras_conferidas: number;
	amostras_aprovadas: number;
	amostras_reprovadas: number;
	itens: AmostragemItem[];
	data_criacao: string;
	data_conclusao: string | null;
}


export const gerarAmostragem = async (
	conferencia_id: number,
): Promise<Amostragem> => {
	try {
		const { data } = await api.post(
			`/reposicao/amostragem/conferencia/${conferencia_id}`,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao gerar amostragem.");
	}
};


export const buscarAmostragemPorConferencia = async (
	conferencia_id: number,
): Promise<Amostragem> => {
	try {
		const { data } = await api.get(
			`/reposicao/amostragem/conferencia/${conferencia_id}`,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao buscar amostragem.");
	}
};


export const buscarAmostragem = async (
	amostragem_id: number,
): Promise<Amostragem> => {
	try {
		const { data } = await api.get(
			`/reposicao/amostragem/${amostragem_id}`,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao buscar amostragem.");
	}
};


export const registrarResultadoAmostragem = async (
	amostragem_id: number,
	item_seq: number,
	quantidade_bipada: number,
	observacao?: string,
): Promise<AmostragemItem> => {
	try {
		const body: { quantidade_bipada: number; observacao?: string } = {
			quantidade_bipada,
		};
		if (observacao) body.observacao = observacao;

		const { data } = await api.patch(
			`/reposicao/amostragem/${amostragem_id}/item/${item_seq}`,
			body,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao registrar resultado da amostragem.");
	}
};


export interface ProdutoReposicao {
	produto_reposicao_id: number;
	quantidade_reposicao: number;
	reposicao: { reposicao_id: number };
	produto_estoque_origem?: {
		produto_estoque_id: number;
		quantidade: number;
		produto?: { produto_id: number; sku: string; descricao: string; ean: string };
		localizacao?: { localizacao_id: number; nome: string };
	};
}


export const buscarProdutosReposicao = async (
	reposicao_id: number,
): Promise<ProdutoReposicao[]> => {
	try {
		const { data } = await api.get(
			`/produto-reposicao/reposicao/${reposicao_id}`,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao buscar produtos da reposição.");
	}
};


export const buscarEstatisticasProdutoReposicao = async (
	reposicao_id: number,
): Promise<any> => {
	try {
		const { data } = await api.get(
			`/produto-reposicao/reposicao/${reposicao_id}/estatisticas`,
		);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err) && err.response?.data?.message) {
			throw new Error(err.response.data.message);
		}
		throw new Error("Falha ao buscar estatísticas da reposição.");
	}
};


// ==================== DETALHE DA RESERVA POR ORIGEM ====================

export interface ReservaDaOrigem {
	/** PEDIDO | FULL | MANUAL | REPOSICAO | TRANSFERENCIA */
	origem_tipo: string;
	/** num_pedido / num_envio / "Reposição #N". */
	referencia: string | null;
	quantidade: number;
	/** Pode ser movida pela reposição interna? É o veredito que decide a origem. */
	realocavel: boolean;
	/** Por que não sai. Null quando é realocável. */
	motivo: string | null;
}

/**
 * Detalhe das reservas de UM produto_estoque.
 *
 * Sob demanda (clique na coluna "Reservada"): montar para a lista inteira
 * sairia caro e ninguém olha todas.
 */
export const buscarReservasDaOrigem = async (
	produto_estoque_id: number,
): Promise<ReservaDaOrigem[]> => {
	const { data } = await api.get(
		`/reposicao/origens/reservas/${produto_estoque_id}`,
	);
	return Array.isArray(data) ? data : [];
};
